import { Icon, usePrompt, useToast } from '@ur-solutions/react-components'
import type { CellProps } from '@/components_deprecated'
import { Button, Cell } from '@/components_deprecated'
import type { ActivitiesDynamicColumnDayReport } from '@/modules/activities/types.graphql'
import { useState, type MouseEventHandler } from 'react'
import { useParam } from '@/utils'
import styled from 'styled-components'
import { SelectAddonType } from './SelectAddonType'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMutation } from '@apollo/client'
import {
  CREATE_FIXED_ADDON,
  CREATE_TIME_ADDON,
} from '@/modules/salary/mutations'
import { addHours, format, formatISO, parseISO } from 'date-fns'
import { EditAddonPrompt } from '@/modules/salary/EditAddonPrompt'
import type {
  FixedAddonForm,
  OverrideOrAddonForm,
} from '@/modules/salary/types'
import { AddonType } from '../../types'
import { EditFixedAddonPrompt } from '@/modules/salary/EditFixedAddonPrompt'
import { ElipsedText } from './DietCell'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  a:hover {
    text-decoration: underline;
  }
`

const ExistingLink = styled.a`
  white-space: pre;
`

export const AddonButton: React.FC<{
  userId: string
  datetimeStart: Date
  datetimeEnd: Date
  addonsAsText: string[]
  restrictWidth?: boolean
  hideText?: boolean
  onClick?: () => void
}> = ({
  userId,
  datetimeStart,
  datetimeEnd,
  addonsAsText,
  restrictWidth,
  hideText,
  onClick,
}) => {
  const translations = useTranslate({
    nExistingAddons: ['common.n-existing-addons', { n: 0 }],
    toast: {
      successCreate: 'salary.time-addon-create-success',
    },
  })
  const addToast = useToast()
  const addPrompt = usePrompt()
  const [open, setOpen] = useState(false)

  const refetchQueries = [
    'AllTimeAddons',
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createTimeAddon] = useMutation(CREATE_TIME_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
    },
  })

  const [createFixedAddon] = useMutation(CREATE_FIXED_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
    },
  })

  async function handleAddTimeAddon() {
    const { data } = await addPrompt<OverrideOrAddonForm | null>(resolve => (
      <EditAddonPrompt
        initialData={{
          datetimeStart: datetimeStart,
          datetimeEnd: datetimeEnd,
          salaryCode: null,
          overrideOtherAddons: false,
          comment: '',
        }}
        userId={userId}
        onSubmit={resolve}
      />
    ))

    if (
      !data ||
      !data.datetimeStart ||
      !data.datetimeEnd ||
      !data.salaryCode ||
      typeof data.comment === 'undefined'
    )
      return

    void createTimeAddon({
      variables: {
        input: {
          user: userId,
          datetimeStart: formatISO(data.datetimeStart),
          datetimeEnd: formatISO(data.datetimeEnd),
          salaryCode: data.salaryCode,
          overrideOtherAddons: data.overrideOtherAddons,
          comment: data.comment,
        },
      },
    })
  }

  async function handleAddFixedAddon() {
    const { data } = await addPrompt<FixedAddonForm | null>(resolve => (
      <EditFixedAddonPrompt
        initialData={{
          date: datetimeStart,
          salaryCode: null,
          amount: 0,
          comment: '',
        }}
        userId={userId}
        onSubmit={resolve}
      />
    ))

    if (!data || !data.salaryCode) return

    void createFixedAddon({
      variables: {
        input: {
          user: userId,
          date: format(data.date, 'yyyy-MM-dd'),
          salaryCode: data.salaryCode,
          amount: data.amount,
          comment: data.comment,
        },
      },
    })
  }

  return (
    <Wrapper>
      {addonsAsText.length > 0 && !hideText && (
        <>
          <ExistingLink
            onClick={evt => {
              evt.stopPropagation()
              onClick?.()
              document
                .getElementById('addon-overview')
                ?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            {addonsAsText.map((text, index) => (
              <ElipsedText key={index} restrictWidth={restrictWidth}>
                {text}
              </ElipsedText>
            ))}
          </ExistingLink>
        </>
      )}
      <Button
        padding="0.25rem 0.75rem !important"
        height="auto"
        onClick={evt => {
          evt.stopPropagation()
          onClick?.()
          setOpen(true)
        }}
      >
        <Icon icon="plus" margin="0" />
      </Button>

      <SelectAddonType
        open={open}
        onClose={() => {
          setOpen(false)
        }}
        onSubmit={type => {
          if (type === AddonType.TIME) {
            void handleAddTimeAddon()
          } else if (type === AddonType.FIXED) {
            void handleAddFixedAddon()
          }
          setOpen(false)
        }}
      />
    </Wrapper>
  )
}

interface AddonCellProps extends CellProps {
  report: Pick<ActivitiesDynamicColumnDayReport, 'addonsAsText' | 'date'>
  subColumn: boolean

  userIdOverride?: string
  datetimeStart?: Date
  datetimeEnd?: Date
  hideText?: boolean

  onClick?: () => void
}
export const AddonCell: React.FC<AddonCellProps> = ({
  narrow = true,
  centered = true,
  subColumn = false,
  report,

  userIdOverride,
  datetimeStart,
  datetimeEnd,
  hideText,

  onClick,
}) => {
  const paramId = useParam('userId', true)
  const userId = userIdOverride ?? paramId

  if (subColumn) return <td></td>

  const handleClick: MouseEventHandler<HTMLTableCellElement> = event =>
    event.stopPropagation()

  return (
    <Cell onClick={handleClick} narrow={narrow} centered={centered}>
      <AddonButton
        onClick={onClick}
        userId={userId ?? ''}
        datetimeStart={datetimeStart ?? parseISO(report.date)}
        datetimeEnd={datetimeEnd ?? addHours(parseISO(report.date), 24)}
        addonsAsText={report.addonsAsText}
        hideText={hideText}
      />
    </Cell>
  )
}
